"use client";

import { useEffect, useRef, useState } from "react";

export interface RunProgress {
  done: number;
  total: number;
}

/**
 * One cancellable network run at a time, for the reference tools that look things up.
 *
 * Starting a run aborts the one before it, and leaving the page aborts whatever is in
 * flight. Progress and completion from a superseded run are ignored, so a slow answer
 * to an old request can never overwrite a newer result.
 */
export function useRun() {
  const current = useRef<AbortController | null>(null);
  const [progress, setProgress] = useState<RunProgress | null>(null);

  useEffect(() => () => current.current?.abort(), []);

  function start(total: number): AbortController {
    current.current?.abort();
    const controller = new AbortController();
    current.current = controller;
    setProgress({ done: 0, total });
    return controller;
  }

  function isCurrent(controller: AbortController): boolean {
    return current.current === controller && !controller.signal.aborted;
  }

  function advance(controller: AbortController, done: number, total: number) {
    if (isCurrent(controller)) {
      setProgress({ done, total });
    }
  }

  function finish(controller: AbortController) {
    if (current.current === controller) {
      setProgress(null);
    }
  }

  function cancel() {
    current.current?.abort();
    current.current = null;
    setProgress(null);
  }

  return {
    progress,
    running: progress !== null,
    start,
    isCurrent,
    advance,
    finish,
    cancel,
  };
}
